import { ClientPlan } from '@maru44/huntre-utils/src/models/client'
import { ClientMemberRole } from '@maru44/huntre-utils/src/models/clientMember'
import type { ClientSubGroup } from '@maru44/huntre-utils/src/models/clientSubGroup'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useCallback, useContext, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { Breads } from 'src/components/atoms/BreadCrumbs'
import { ClientSubGroupFormDialog } from 'src/components/dialogs/clientSubGroup/ClientSubGroupFormDialog'
import { ProSection } from 'src/components/organisms/pro/ProSection'
import { ClientContext } from 'src/components/providers/ClientProvider'
import { useClientDevices } from 'src/hooks/clientDevice/useClientDevices'
import { useClientSubGroups } from 'src/hooks/clientSubGroup/useClientSubGroups'
import { capture } from 'src/utils/sentry'

const FIRST_CREATE_WARNING = '初めてサブグループを作成すると、どのサブグループにも含まれないデバイスはオーナーのみに通知されるようになります。'

export const ClientSubGroups = () => {
  const { client, currentMember, clientMembers, clientSubGroups, proSubscription, isProSubscriptionLoading } = useContext(ClientContext)
  const clientId = client?.id ?? ''

  const { createClientSubGroup, deleteClientSubGroup } = useClientSubGroups(clientId)
  const { listClientDevicesWithDevice } = useClientDevices(clientId)

  const [openCreate, setOpenCreate] = useState(false)

  const isAdmin = currentMember?.role === ClientMemberRole.Admin
  // admin は Stripe の状態 (proSubscription.isEffectivelyPro) を真実とする
  // (active でも初回振込前は false なので、未払いユーザーは Pro 扱いしない)
  // 非 admin は Firestore の plan に従う (Stripe を呼べないため)
  const isPro = isAdmin ? !!proSubscription?.isEffectivelyPro : client?.plan === ClientPlan.Pro
  const isPendingPayment = isAdmin && !!proSubscription && !proSubscription.isEffectivelyPro
  const isFirstCreate = clientSubGroups.length === 0

  const handleCreate = useCallback(
    async (input: Parameters<typeof createClientSubGroup>[0]) => {
      await createClientSubGroup(input)
    },
    [createClientSubGroup],
  )

  const handleDelete = useCallback(
    async (sg: ClientSubGroup) => {
      if (!confirm(`サブグループ「${sg.name}」を削除しますか?`)) return
      try {
        await deleteClientSubGroup(sg.id)
        toast.success('削除しました')
      } catch (e) {
        capture(e)
        toast.error('削除に失敗しました')
      }
    },
    [deleteClientSubGroup],
  )

  // admin は Stripe チェックの完了を待つ
  if (isAdmin && isProSubscriptionLoading) {
    return (
      <Box>
        <Breads breadIds={['subGroups']} />
        <Stack
          spacing={2}
          sx={{
            alignItems: 'center',
            py: 4,
          }}
        >
          <CircularProgress size={28} />
        </Stack>
      </Box>
    )
  }

  return (
    <>
      <Box>
        <Breads breadIds={['subGroups']} />
        <Stack spacing={2}>
          <Typography variant="h5">サブグループ</Typography>
          <Typography>デバイスとメンバーをグループ化して通知の対象を分けられます。</Typography>
          {isPendingPayment && <Typography>Pro プランのお支払いが確認できしだいご利用いただけます。</Typography>}
          <ProSection clientId={clientId} />
          {isPro && (
            <>
              <TableContainer component={Paper} elevation={6}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>名前</TableCell>
                      <TableCell>デバイス数</TableCell>
                      <TableCell>メンバー数</TableCell>
                      <TableCell>LINE通知先</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientSubGroups.map((sg) => (
                      <TableRow key={sg.id}>
                        <TableCell>
                          <Link to={`/clients/${clientId}/subGroups/${sg.id}`}>{sg.name}</Link>
                        </TableCell>
                        <TableCell>{sg.clientDeviceIds.length}</TableCell>
                        <TableCell>{sg.clientMemberIds.length}</TableCell>
                        <TableCell>{sg.lineTarget ? 'あり' : 'なし'}</TableCell>
                        <TableCell align="right">
                          {isAdmin && (
                            <IconButton size="small" onClick={() => handleDelete(sg)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {clientSubGroups.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography
                            sx={{
                              color: 'text.secondary',
                            }}
                          >
                            サブグループはまだありません
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {isAdmin && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'end',
                  }}
                >
                  <Button variant="contained" onClick={() => setOpenCreate(true)}>
                    サブグループを作成
                  </Button>
                </Box>
              )}
            </>
          )}
        </Stack>
      </Box>
      <ClientSubGroupFormDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSubmit={handleCreate}
        clientDevices={(listClientDevicesWithDevice.data ?? []).map((v) => v.clientDevice)}
        clientMembers={clientMembers}
        warningMessage={isFirstCreate ? FIRST_CREATE_WARNING : undefined}
      />
    </>
  )
}
